import { useTranslation } from 'next-i18next';
import styled from 'styled-components';
import ImageWrapper from '../../../new-components/ImageWrapper';
import PlayIcon from '../../../new-components/svg-icons/PlayIcon';
import { useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useVideo } from '../../../hooks/Video';

interface PropertyVirtualTourBannerProps {
  walkthroughVideo: string;
  walkthroughVideoCoverImage: string;
  propertyData: {
    property_id: number;
    compound_id: number;
    compound_name: string;
    sale_type: string;
    property_type: string;
  };
}

const PropertyVirtualTourBanner = ({
  walkthroughVideo,
  walkthroughVideoCoverImage,
  propertyData
}: PropertyVirtualTourBannerProps) => {
  const { locale } = useRouter();
  const { t } = useTranslation('entity');

  const [isVideoExpanded, setIsVideoExpanded] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const { handleFullScreenVideo } = useVideo({
    videoRef,
    propertyData,
    setIsVideoExpanded,
    eventName: 'virtual_tour_click',
    featureName: 'walkthrough_video_property_page'
  });

  return (
    <>
      <BannerContainer
        locale={locale}
        onClick={() => {
          handleFullScreenVideo('virtual_tour_banner');
        }}
      >
        <ImageWrapper
          src={walkthroughVideoCoverImage}
          alt={walkthroughVideoCoverImage ?? 'video_cover_image'}
          style={{
            objectFit: 'cover',
            zIndex: -1
          }}
          imageSize="default"
          loading="eager"
          fill
        />
        <div className="play-icon-container">
          <PlayIcon />
        </div>
        <span className="unit-virtual-tour">{t('unit_virtual_tour')}</span>
      </BannerContainer>

      {walkthroughVideo && (
        <HiddenVideo
          ref={videoRef}
          src={walkthroughVideo}
          playsInline
          preload="auto"
          controls={isVideoExpanded}
          muted={!isVideoExpanded}
          loop
        />
      )}
    </>
  );
};

const BannerContainer = styled.div<any>`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: 549px;
  height: 207px;
  border-radius: 19px;
  overflow: hidden;
  cursor: pointer;
  background: linear-gradient(
    180deg,
    rgba(127, 127, 127, 0.16) -7%,
    rgba(0, 0, 0, 0.8) 109.8%
  );

  &:hover {
    background: linear-gradient(
      180deg,
      rgba(90, 90, 90, 0.25) -7%,
      rgba(0, 0, 0, 0.85) 109.8%
    );
  }

  box-shadow: 0 1px 7px 0 rgba(0, 0, 0, 0.08);

  .unit-virtual-tour {
    position: absolute;
    bottom: 15px;
    ${props => (props.locale == 'en' ? 'left: 15px' : 'right: 15px')};
    color: #fff;
    font-size: 20px;
    font-family: materialSemibold;
    line-height: 140%;
    letter-spacing: 0.4px;
  }

  .play-icon-container {
    z-index: 99;
  }

  @media screen and (max-width: ${props =>
      props.theme.breakPoints.mobile.max}) {
    width: auto;
    height: 164px;
  }
`;

const HiddenVideo = styled.video`
  visibility: hidden;
  width: 1px;
  height: 1px;

  &:fullscreen {
    visibility: visible;
    width: 100%;
    height: 100%;
  }
`;

export default PropertyVirtualTourBanner;
